import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ShopHeader from '../../components/customer/ShopHeader';
import ShopSidebar from '../../components/customer/ShopSidebar';
import ProductCard from '../../components/customer/ProductCard';
import Loader from '../../components/common/Loader';
import api from '../../utils/api';

const Shop = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]); // ✅ ADDED: Brands state
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null); // ✅ ADDED: Selected brand state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(12);

  // Read category and brand from URL on mount
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const brandParam = searchParams.get('brand'); // ✅ ADDED: Read brand from URL
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
    if (brandParam) {
      setSelectedBrand(brandParam);
    }
  }, [searchParams]);

  // Fetch categories and brands on mount
  useEffect(() => {
    fetchCategories();
    fetchBrands(); // ✅ ADDED: Fetch brands
  }, []);

  // Fetch products when filters change
  useEffect(() => {
    fetchProducts();
    setCurrentPage(1);
  }, [selectedCategory, selectedBrand, searchTerm, sortBy, priceRange]); // ✅ ADDED: selectedBrand

  // Re-fetch when language changes
  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchBrands(); // ✅ ADDED: Re-fetch brands on language change
  }, [i18n.language]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/products/categories');
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // ✅ ADDED: Fetch brands function
  const fetchBrands = async () => {
    try {
      const response = await api.get('/products/brands');
      setBrands(response.data.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {
        category: selectedCategory,
        brand: selectedBrand, // ✅ ADDED: Brand filter
        search: searchTerm,
        sort: sortBy,
        min_price: priceRange.min,
        max_price: priceRange.max
      };

      const response = await api.get('/products', { params });
      setProducts(response.data.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    const params = {};
    if (category) params.category = category;
    if (selectedBrand) params.brand = selectedBrand;
    setSearchParams(params);
  };

  // ✅ ADDED: Handle brand change
  const handleBrandChange = (brand) => {
    setSelectedBrand(brand);
    const params = {};
    if (selectedCategory) params.category = selectedCategory;
    if (brand) params.brand = brand;
    setSearchParams(params);
  };

  const handleClearFilters = () => {
    setSelectedCategory(null);
    setSelectedBrand(null); // ✅ ADDED: Clear brand
    setSearchTerm('');
    setPriceRange({ min: '', max: '' });
    setSortBy('newest');
    setSearchParams({});
  };

  // Pagination logic
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = products.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(products.length / productsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  return (
    <div className="shop-layout">
      <ShopSidebar
        onSearch={setSearchTerm}
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
        brands={brands} // ✅ ADDED: Pass brands
        selectedBrand={selectedBrand} // ✅ ADDED: Pass selected brand
        onBrandChange={handleBrandChange} // ✅ ADDED: Pass brand change handler
        priceRange={priceRange}
        onPriceChange={setPriceRange}
        onClearFilters={handleClearFilters}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="shop-main">
        <div className="shop-content">
          <div className="products-header">
            <div className="products-count">
              {products.length} Products
              {searchTerm && (
                <span style={{ color: 'var(--gray)', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                  "{searchTerm}"
                </span>
              )}
              {selectedCategory && (
                <span style={{ color: 'var(--primary)', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                  {t('shop.in')} {categories.find(c => c.slug === selectedCategory)?.name || selectedCategory}
                </span>
              )}
              {/* ✅ ADDED: Show selected brand */}
              {selectedBrand && (
                <span style={{ color: 'var(--primary)', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                  • {brands.find(b => b.slug === selectedBrand)?.name || selectedBrand}
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <Loader />
          ) : products.length > 0 ? (
            <>
              <div className="products-grid">
                {currentProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-container">
                  <button
                    className="pagination-btn pagination-arrow"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                  >
                    ← {t('shop.previous')}
                  </button>

                  <div className="pagination-numbers">
                    {getPageNumbers().map((number, index) => (
                      number === '...' ? (
                        <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                          ...
                        </span>
                      ) : (
                        <button
                          key={number}
                          onClick={() => paginate(number)}
                          className={`pagination-btn pagination-number ${
                            currentPage === number ? 'active' : ''
                          }`}
                        >
                          {number}
                        </button>
                      )
                    ))}
                  </div>

                  <button
                    className="pagination-btn pagination-arrow"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                  >
                    {t('shop.next')} →
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="products-empty">
              <div className="products-empty-icon">🔍</div>
              <h3>{t('shop.noProducts')}</h3>
              <p>{t('shop.tryDifferent')}</p>
            </div>
          )}
        </div>
      </div>

      <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
        🔍
      </button>
    </div>
  );
};

export default Shop;